import React from 'react';
import { Box, Button, Flex, SimpleGrid, Spinner, Text, useToast } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { ArrowCounterClockwise, Lightning, MagicWand, Sparkle } from 'phosphor-react';

import { IEmpresaOption } from '../../../../../../hooks/useContaCilImportar';
import {
  IIaResultado,
  useContaCilIaConciliar,
  useContaCilIaGerar,
  useContaCilIaLog,
  useContaCilIaReverter,
} from '../../../../../../hooks/useContaCilRegras';
import { extractApiErrorMessage } from '../../../../../../utils/ErrorUtils';
import { C, fmtData } from '../regrasHelpers';

interface RegrasIaTabProps {
  empresa: IEmpresaOption | null;
}

const fmtUsd = (v?: number | null) => `US$ ${(v ?? 0).toFixed(4)}`;

export function RegrasIaTab({ empresa }: RegrasIaTabProps) {
  const toast = useToast();
  const gerar = useContaCilIaGerar();
  const conciliar = useContaCilIaConciliar();
  const reverter = useContaCilIaReverter();
  const { data: log = [], isFetching } = useContaCilIaLog(empresa?.value);
  const [ultimo, setUltimo] = React.useState<IIaResultado | null>(null);

  if (!empresa) {
    return (
      <Box bg="rgba(255,255,255,0.65)" borderRadius="3xl" p={10} textAlign="center">
        <Text fontSize="sm" color={C.muted}>
          Selecione uma empresa para usar a IA.
        </Text>
      </Box>
    );
  }

  const rodar = async (doMutate: () => Promise<IIaResultado>, label: string) => {
    try {
      const res = await doMutate();
      setUltimo(res);
      toast({
        title: `${label}: ${res.regras_criadas} regra(s), ${res.conciliados} conciliado(s)`,
        description: `Custo ${fmtUsd(res.custo_usd)} · ${res.descartados_baixa_confianca} descartado(s) por baixa confiança`,
        status: 'success',
        duration: 7000,
      });
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, 'Falha na IA.'), status: 'error' });
    }
  };

  const onReverter = async (id: number) => {
    try {
      const res = await reverter.mutateAsync(id);
      toast({
        title: `Revertido: ${res.regras_removidas} regra(s), ${res.lancamentos_revertidos} lançamento(s)`,
        status: 'success',
      });
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, 'Falha ao reverter.'), status: 'error' });
    }
  };

  const card = (
    titulo: string,
    desc: string,
    icon: React.ReactNode,
    onClick: () => void,
    loading: boolean,
    accent: string,
  ) => (
    <Box
      bg="rgba(255,255,255,0.72)"
      backdropFilter="blur(28px) saturate(200%)"
      sx={{ WebkitBackdropFilter: 'blur(28px) saturate(200%)' }}
      borderWidth="1px"
      borderColor="rgba(255,255,255,0.6)"
      borderRadius="2xl"
      p={5}
    >
      <Flex align="center" gap={3} mb={3}>
        <Box
          w="44px"
          h="44px"
          borderRadius="xl"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg={`${accent}1A`}
          color={accent}
        >
          {icon}
        </Box>
        <Box>
          <Text fontSize="sm" fontWeight="700" color={C.texto}>
            {titulo}
          </Text>
          <Text fontSize="11px" color={C.muted}>
            {desc}
          </Text>
        </Box>
      </Flex>
      <Button
        w="full"
        borderRadius="full"
        color="white"
        fontWeight="600"
        bgGradient={`linear(135deg, ${accent}, ${C.rosa})`}
        boxShadow={`0 6px 16px -4px ${accent}66`}
        _hover={{ filter: 'brightness(1.05)', transform: 'translateY(-1px)' }}
        isLoading={loading}
        leftIcon={<Sparkle size={16} weight="fill" />}
        onClick={onClick}
      >
        Rodar
      </Button>
    </Box>
  );

  return (
    <Flex direction="column" gap={5}>
      <Box
        bg="rgba(99,102,241,0.06)"
        borderWidth="1px"
        borderColor="rgba(99,102,241,0.18)"
        borderRadius="xl"
        p={4}
      >
        <Flex align="center" gap={2} color={C.indigo}>
          <MagicWand size={16} weight="duotone" />
          <Text fontSize="xs" fontWeight="600">
            A IA (Claude Sonnet) aplica automaticamente as sugestões de alta confiança. Tudo fica
            registrado abaixo e é reversível.
          </Text>
        </Flex>
      </Box>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        {card(
          'Gerar regras com IA',
          'Lê os gaps em "Outros" + o plano de contas e cria regras.',
          <Lightning size={24} weight="duotone" />,
          () => rodar(() => gerar.mutateAsync(empresa.value), 'Regras geradas'),
          gerar.isPending,
          C.indigo,
        )}
        {card(
          'Conciliar pendentes com IA',
          'Sugere categoria + POC direto nos lançamentos pendentes.',
          <MagicWand size={24} weight="duotone" />,
          () => rodar(() => conciliar.mutateAsync({ empresaId: empresa.value }), 'Conciliação IA'),
          conciliar.isPending,
          C.brand,
        )}
      </SimpleGrid>

      {ultimo && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <Box
            bg="rgba(16,185,129,0.08)"
            borderWidth="1px"
            borderColor="rgba(16,185,129,0.2)"
            borderRadius="xl"
            p={4}
          >
            <Text fontSize="sm" fontWeight="700" color={C.verdeText} mb={1}>
              Última execução ({ultimo.modo})
            </Text>
            <Text fontSize="xs" color={C.muted}>
              {ultimo.regras_criadas} regra(s) · {ultimo.conciliados} conciliado(s) ·{' '}
              {ultimo.analisados} analisado(s) · {ultimo.descartados_baixa_confianca} descartado(s){' '}
              · custo {fmtUsd(ultimo.custo_usd)} · {ultimo.modelo}
            </Text>
          </Box>
        </motion.div>
      )}

      {/* Log */}
      <Box
        bg="rgba(255,255,255,0.65)"
        backdropFilter="blur(28px) saturate(200%)"
        sx={{ WebkitBackdropFilter: 'blur(28px) saturate(200%)' }}
        borderWidth="1px"
        borderColor="rgba(255,255,255,0.6)"
        borderRadius="3xl"
        overflow="hidden"
      >
        <Flex
          align="center"
          justify="space-between"
          px={4}
          py={3}
          borderBottomWidth="1px"
          borderColor={C.hairline}
        >
          <Text fontSize="sm" fontWeight="600" color={C.texto}>
            Execuções da IA
            {isFetching && <Spinner size="xs" ml={2} color={C.brand} thickness="2px" />}
          </Text>
        </Flex>
        {log.length === 0 ? (
          <Box p={8} textAlign="center">
            <Text fontSize="sm" color={C.muted}>
              Nenhuma execução ainda.
            </Text>
          </Box>
        ) : (
          log.map((ex, idx) => (
            <Flex
              key={ex.id}
              align="center"
              gap={3}
              px={4}
              py={3}
              borderBottomWidth={idx < log.length - 1 ? '1px' : '0'}
              borderColor="rgba(15,23,42,0.05)"
              opacity={ex.revertida ? 0.55 : 1}
            >
              <Box flex={1} minW={0}>
                <Text fontSize="sm" fontWeight="600" color={C.texto}>
                  {ex.modo === 'gerar_regras' ? 'Gerar regras' : 'Conciliar'}
                  {ex.revertida && (
                    <Text as="span" fontSize="10px" color={C.muted} ml={2}>
                      (revertida)
                    </Text>
                  )}
                </Text>
                <Text fontSize="11px" color={C.muted}>
                  {ex.dt_criacao ? fmtData(ex.dt_criacao.slice(0, 10)) : ''} ·{' '}
                  {ex.qtd_regras_criadas} regra(s) · {ex.qtd_lancamentos_conciliados} lanç. ·{' '}
                  {fmtUsd(ex.custo_usd)} · {ex.modelo}
                </Text>
              </Box>
              {!ex.revertida && (
                <Button
                  size="xs"
                  variant="ghost"
                  borderRadius="full"
                  color={C.muted}
                  leftIcon={<ArrowCounterClockwise size={14} />}
                  isLoading={reverter.isPending}
                  _hover={{ bg: 'rgba(239,68,68,0.10)', color: C.vermelho }}
                  onClick={() => onReverter(ex.id)}
                >
                  Reverter
                </Button>
              )}
            </Flex>
          ))
        )}
      </Box>
    </Flex>
  );
}
